import type {OAuthSession} from "@atproto/oauth-client-browser";
import {createContext, useContext, useEffect, useMemo, useState} from "react";
import {logger} from "@/app/events";
import {client} from "../client";

interface AuthContextState {
  error: Error | null;
  isLoading: boolean;
  session: OAuthSession | null;
}

interface AuthContextActions {
  login(handle: string): void;
  loginCallback(): void;
  logout(): void;
}

type AuthContextValue = [AuthContextState, AuthContextActions];

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function useAuth() {
  const value = useContext(AuthContext);
  if (value == null) throw new Error("useAuth must be called inside AuthProvider");
  return value;
}

export function AuthProvider(props: {children: React.ReactNode}) {
  const [isLoading, setIsLoading] = useState(true);
  const [session, setSession] = useState<OAuthSession | null>(null);
  const [error, setError] = useState<Error | null>(null);

  const nonce = useMemo(() => {
    let value = localStorage.getItem("oauth-nonce");
    if (!value) {
      value = Math.random().toString(16).slice(2);
      localStorage.setItem("oauth-nonce", value);
    }
    return value;
  }, []);

  useEffect(() => {
    client
      .init()
      .then(result => {
        if (!result) {
          return logger.auth.debug("Couldn't retrieve valid session");
        }
        setSession(result.session);
        const message = `${result.session.sub} was restored (last active session)`;
        logger.auth.debug(message, result);
      })
      .catch(err => {
        logger.auth.error("Session initialization failed", {error: err});
        setError(err);
      })
      .finally(() => setIsLoading(false));
  }, []);

  const actions = useMemo(
    () => ({
      login(handle: string) {
        setIsLoading(true);
        return client
          .signIn(handle, {state: nonce})
          .catch(err => {
            logger.auth.error("Login call failed", {error: err});
            setError(err);
          })
          .finally(() => setIsLoading(false));
      },
      loginCallback() {
        setIsLoading(true);
        return client
          .initCallback()
          .then(
            () => {
              const url = new URL("/profile", window.location.origin);
              window.history.replaceState({}, "", url);
            },
            err => {
              logger.auth.error("OAuth callback failed", {error: err});
              setError(err);
            },
          )
          .finally(() => setIsLoading(false));
      },
      logout() {
        return session?.signOut().then(
          () => {
            setSession(null);
          },
          err => {
            logger.auth.error("Logout call failed", {error: err});
            setError(err);
          },
        );
      },
    }),
    [session, nonce],
  );

  const value = useMemo((): AuthContextValue => {
    const status = {isLoading, session, error};
    return [status, actions] as const;
  }, [actions, isLoading, session, error]);

  return <AuthContext.Provider value={value}>{props.children}</AuthContext.Provider>;
}
